if (typeof customElements.get('infinite-scroll') === 'undefined') {
  class InfiniteScroll extends HTMLElement {
    #abortController = null;
    #observer = null;
    #loading = false;
    #focusNewCards = false;

    connectedCallback() {
      if (this.#observer) return;

      this.grid = this.querySelector('#main-collection-product-grid');
      this.button = this.querySelector('[data-infinite-scroll-next]');
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.pagination = this.querySelector('[data-infinite-scroll-pagination]');

      if (!this.grid || !this.button) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.button.addEventListener('click', this.#onClick.bind(this), { signal });
      this.classList.add('is-ready');

      if (this.pagination) {
        this.pagination.setAttribute('aria-hidden', 'true');
        this.pagination.setAttribute('inert', '');
      }

      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.#loadNext();
        },
        { rootMargin: '0px 0px 800px 0px' }
      );
      this.#observer.observe(this.button);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
      this.#observer = null;
    }

    #onClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.#focusNewCards = true;
      this.#loadNext();
    }

    async #loadNext() {
      if (this.#loading) return;

      const nextUrl = this.#nextUrl();
      if (!nextUrl) {
        this.#finish();
        return;
      }

      this.#loading = true;
      this.#setBusy(true);
      this.#announce(this.dataset.loading);

      try {
        const response = await fetch(nextUrl, {
          signal: this.#abortController?.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = document.createElement('div');
        html.innerHTML = await response.text();
        this.#appendPage(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.#announce(this.dataset.error);
        this.#setBusy(false);
        this.#loading = false;
      }
    }

    #nextUrl() {
      const href = this.button?.getAttribute('href');
      if (!href) return null;

      const url = new URL(href, window.location.href);
      const sectionId = this.grid.dataset.id;
      if (sectionId) url.searchParams.set('section_id', sectionId);
      return url;
    }

    #appendPage(parsedHTML) {
      const newCards = parsedHTML.querySelectorAll(
        '#main-collection-product-grid [data-js-product-item]'
      );
      if (!newCards.length) {
        this.#finish();
        return;
      }

      const cards = [];
      const fragment = document.createDocumentFragment();
      for (const card of newCards) {
        fragment.append(card);
        cards.push(card);
      }

      const existingCards = this.grid.querySelectorAll('[data-js-product-item]');
      const lastCard = existingCards[existingCards.length - 1] || this.grid.lastElementChild;
      if (lastCard) lastCard.after(fragment);
      else this.grid.append(fragment);

      for (const card of cards) {
        for (const template of card.querySelectorAll('template')) {
          template.closest('form')?.append(template.content.cloneNode(true));
        }
      }

      this.#updateNextLink(parsedHTML);
      this.#announce(
        (this.dataset.loaded || '').replace('__COUNT__', String(cards.length))
      );

      document.dispatchEvent(
        new CustomEvent('krown:main-collection-grid:appended', {
          bubbles: true,
          detail: { gridElement: this.grid, cards },
        })
      );

      if (this.#focusNewCards) {
        cards[0].querySelector('a[href]')?.focus();
        this.#focusNewCards = false;
      }

      this.#setBusy(false);
      this.#loading = false;

      if (!this.button?.getAttribute('href')) {
        this.#finish();
        return;
      }

      if (this.#observer && this.button) {
        this.#observer.unobserve(this.button);
        this.#observer.observe(this.button);
      }
    }

    #updateNextLink(parsedHTML) {
      const nextLink = parsedHTML.querySelector('.kt-pagination .kt-next a');
      const pagination = parsedHTML.querySelector('[data-infinite-scroll-pagination]');

      if (pagination && this.pagination) {
        this.pagination.innerHTML = pagination.innerHTML;
      }

      if (!this.button) return;

      if (nextLink) {
        this.button.setAttribute('href', nextLink.getAttribute('href'));
        return;
      }

      this.button.removeAttribute('href');
    }

    #finish() {
      this.#observer?.disconnect();
      this.#setBusy(false);
      this.#loading = false;
      this.button?.setAttribute('hidden', '');
      this.classList.add('is-complete');
    }

    #setBusy(isBusy) {
      this.setAttribute('aria-busy', String(isBusy));
      this.button?.classList.toggle('kt-working', isBusy);
      if (isBusy) this.button?.setAttribute('aria-disabled', 'true');
      else this.button?.removeAttribute('aria-disabled');
    }

    #announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
